import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './useAuth'
import { useTenant } from './useTenant'
import { listTenantUsers } from '@/services/api'

type Role = 'super_admin' | 'owner' | 'admin' | 'editor' | 'viewer'

interface RoleContextType {
  role: Role
  isLoading: boolean
  canManageTeam: boolean
  canEditContent: boolean
  canViewOnly: boolean
  isSuperAdmin: boolean
  isOwner: boolean
  isAdmin: boolean
  isEditor: boolean
  isViewer: boolean
}

const RoleContext = createContext<RoleContextType | undefined>(undefined)

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, isSuperAdmin } = useAuth()
  const { tenantId } = useTenant()
  const [role, setRole] = useState<Role>(isSuperAdmin ? 'super_admin' : 'viewer')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (isSuperAdmin) {
      setRole('super_admin')
      setIsLoading(false)
      return
    }
    if (!tenantId || !user?.email) {
      setIsLoading(false)
      return
    }
    loadUserRole()
  }, [tenantId, user?.email, isSuperAdmin])

  async function loadUserRole() {
    try {
      setIsLoading(true)
      const data = await listTenantUsers(tenantId!)
      const currentUser = data.users?.find(
        (u) => u.email.toLowerCase() === user!.email.toLowerCase()
      )
      setRole((currentUser?.role as Role) || 'viewer')
    } catch {
      // Fallback to least privilege
      setRole('viewer')
    } finally {
      setIsLoading(false)
    }
  }

  // While loading, default to permissive so nav items don't flicker/hide
  const resolved = !isLoading
  const value: RoleContextType = {
    role,
    isLoading,
    canManageTeam: !resolved || ['super_admin', 'owner', 'admin'].includes(role),
    canEditContent: !resolved || ['super_admin', 'owner', 'admin', 'editor'].includes(role),
    canViewOnly: resolved && role === 'viewer',
    isSuperAdmin: role === 'super_admin',
    isOwner: role === 'owner',
    isAdmin: role === 'admin',
    isEditor: role === 'editor',
    isViewer: resolved && role === 'viewer',
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

export function useRole() {
  const context = useContext(RoleContext)
  if (!context) throw new Error('useRole must be used within RoleProvider')
  return context
}
